import './index.css';

import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { CartProvider } from './context/CartContext';

const ASSET_RECOVERY_SESSION_KEY = '__asset_recovery_ran__';

function looksLikeStaleAssetError(message: string): boolean {
  const msg = String(message || '').toLowerCase();
  return (
    msg.includes('not a valid javascript mime type') ||
    msg.includes('text/html') ||
    msg.includes('failed to fetch dynamically imported module') ||
    msg.includes('importing a module script failed') ||
    msg.includes('loading chunk') ||
    msg.includes('chunkloaderror')
  );
}

function clearCookiesForCurrentDomain(): void {
  const parts = window.location.hostname.split('.');
  const domains = new Set<string>();
  for (let i = 0; i < parts.length; i += 1) {
    const d = parts.slice(i).join('.');
    if (d) domains.add(d);
  }
  domains.add(window.location.hostname);

  const cookies = document.cookie.split(';');
  const paths = ['/', window.location.pathname || '/'];
  cookies.forEach((cookie) => {
    const eq = cookie.indexOf('=');
    const name = (eq > -1 ? cookie.slice(0, eq) : cookie).trim();
    if (!name) return;
    domains.forEach((domain) => {
      paths.forEach((path) => {
        document.cookie = `${name}=; Max-Age=0; path=${path}`;
        document.cookie = `${name}=; Max-Age=0; path=${path}; domain=.${domain}`;
      });
    });
  });
}

async function purgeRuntimeCaches(): Promise<void> {
  try {
    localStorage.clear();
  } catch {
    // no-op
  }
  try {
    sessionStorage.clear();
  } catch {
    // no-op
  }
  try {
    clearCookiesForCurrentDomain();
  } catch {
    // no-op
  }

  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // no-op
  }

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    // no-op
  }
}

async function recoverFromStaleAssets() {
  const alreadyRan = sessionStorage.getItem(ASSET_RECOVERY_SESSION_KEY) === '1';
  if (alreadyRan) return;
  sessionStorage.setItem(ASSET_RECOVERY_SESSION_KEY, '1');

  await purgeRuntimeCaches();

  const next = new URL(window.location.href);
  next.searchParams.set('__asset_recovery_ts', Date.now().toString());
  window.location.replace(next.toString());
}

function installStaleAssetRecovery() {
  window.addEventListener('error', (event) => {
    const errText = String(event.message || event.error?.message || '');
    if (looksLikeStaleAssetError(errText)) {
      void recoverFromStaleAssets();
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const errText =
      typeof reason === 'string'
        ? reason
        : String(reason?.message || reason?.toString?.() || '');
    if (looksLikeStaleAssetError(errText)) {
      void recoverFromStaleAssets();
    }
  });
}

installStaleAssetRecovery();

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <CartProvider>
      <App />
    </CartProvider>
  </React.StrictMode>
);
